import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { COMPANY } from '../../data/content';
import LanguageSwitcher from '../LanguageSwitcher';

export default function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const links = [
    { to: '/', key: 'home' },
    { to: '/about', key: 'about' },
    { to: '/portfolio', key: 'portfolio' },
    { to: '/leadership', key: 'leadership' },
    { to: '/insights', key: 'insights' },
    { to: '/contact', key: 'contact' },
  ];

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-[#F5EFE1]/85 backdrop-blur-xl border-b border-[#0B1F3F]/10'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="flex justify-between items-center py-5 md:py-6 px-4 sm:px-6 md:px-12 gap-3 sm:gap-4">
        <Link to="/" data-testid="nav-logo" className="group flex items-center gap-2 sm:gap-3 min-w-0">
          <img
            src={COMPANY.markWhite}
            alt={COMPANY.fullName}
            className="h-10 sm:h-12 md:h-14 w-auto select-none drop-shadow-[0_0_18px_rgba(196,46,44,0.35)] shrink-0"
            draggable="false"
          />
          <div className="flex flex-col leading-none min-w-0">
            <span className="font-serif text-[12px] sm:text-[16px] md:text-[19px] tracking-[0.08em] sm:tracking-[0.15em] uppercase text-[#0B1F3F] truncate">
              {t('company.short')}
            </span>
            <span className="block text-[7px] sm:text-[9px] md:text-[10px] tracking-[0.2em] sm:tracking-[0.35em] uppercase text-[#6B5B4A] mt-1 truncate">
              {t('company.capital_ag_zug_est')}
            </span>
          </div>
        </Link>

        <nav className="hidden xl:flex items-center gap-8">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              data-testid={`nav-link-${link.key}`}
              className={({ isActive }) =>
                `nav-link text-[12px] tracking-[0.15em] uppercase font-sans transition-colors ${
                  isActive ? 'text-[#0B1F3F] active' : 'text-[#6B5B4A] hover:text-[#0B1F3F]'
                }`
              }
            >
              {t(`nav.${link.key}`)}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <div>
            <LanguageSwitcher variant="header" />
          </div>

          <Link
            to="/contact"
            data-testid="nav-cta-contact"
            className="hidden xl:inline-flex items-center px-5 py-3 border border-[#0B1F3F]/25 text-[11px] tracking-[0.2em] uppercase text-[#0B1F3F] hover:bg-[#B58F45] hover:border-[#B58F45] transition-all duration-300"
          >
            {t('nav.investor_relations')}
          </Link>

          <button
            onClick={() => setOpen((o) => !o)}
            className="xl:hidden text-[#0B1F3F]"
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        data-testid="nav-mobile-panel"
        className={`xl:hidden overflow-hidden transition-[max-height] duration-500 border-t border-[#0B1F3F]/10 bg-[#F5EFE1] ${
          open ? 'max-h-[640px]' : 'max-h-0'
        }`}
      >
        <div className="flex flex-col px-6 py-8 gap-6">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              data-testid={`nav-mobile-link-${link.key}`}
              className={({ isActive }) =>
                `text-lg font-serif ${isActive ? 'text-[#B58F45]' : 'text-[#0B1F3F]'}`
              }
            >
              {t(`nav.${link.key}`)}
            </NavLink>
          ))}

          <div className="pt-4 border-t border-[#0B1F3F]/10">
            <LanguageSwitcher variant="mobile" />
          </div>

          <Link
            to="/contact"
            data-testid="nav-mobile-cta"
            className="mt-2 inline-flex justify-center px-6 py-3 border border-[#0B1F3F]/25 text-[11px] tracking-[0.2em] uppercase"
          >
            {t('nav.investor_relations')}
          </Link>
        </div>
      </div>
    </header>
  );
}
